import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { 
  Autocomplete, 
  TextField, 
  CircularProgress, 
  Box, 
  Typography,
  Paper,
  Popper,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import StoreIcon from '@mui/icons-material/Store';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';

// Debounce function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Enhanced search component with debouncing and custom rendering
 */
function EnhancedSearch({ 
  endpoint = '/organizations/search', 
  label = 'Search', 
  placeholder = 'Type to search...', 
  minChars = 2,
  debounceMs = 300,
  onSelect,
  renderOption,
  getOptionLabel,
  filterOptions,
  autoFocus = false,
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  limitResults = 10,
  textFieldProps = {},
  autocompleteProps = {},
  value = null
}) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const searchAbortController = useRef(null);

  // Default getOptionLabel if not provided
  const defaultGetOptionLabel = useCallback((option) => {
    if (typeof option === 'string') return option;
    return option.name || option.title || option.id?.toString() || '';
  }, []);

  // Sync inputValue with value prop when value changes
  useEffect(() => {
    if (value) {
      // Value is a resolved object — sync the displayed text
      setInputValue(getOptionLabel ? getOptionLabel(value) : defaultGetOptionLabel(value));
    } else if (value === null) {
      // Explicitly null means the parent cleared the field — reset input text
      setInputValue('');
    }
    // value === undefined means the org exists but isn't in the local list yet — don't clear
  }, [value, getOptionLabel, defaultGetOptionLabel]);

  // Default renderOption if not provided
  const defaultRenderOption = useCallback((props, option, { inputValue }) => {
    // Determine icon based on business type
    let icon = <BusinessIcon />;
    if (option.businessType) {
      const type = option.businessType.toLowerCase();
      if (type.includes('school')) {
        icon = <SchoolIcon />;
      } else if (type.includes('retail') || type.includes('shop')) {
        icon = <StoreIcon />;
      }
    }

    // Format address info
    const addressParts = [];
    if (option.suburb) addressParts.push(option.suburb);
    if (option.state) addressParts.push(option.state);
    if (option.postcode) addressParts.push(option.postcode);
    const addressText = addressParts.join(', ');
    
    return (
      <ListItem {...props} alignItems="flex-start">
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {icon}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={option.name || defaultGetOptionLabel(option)}
          secondary={
            <React.Fragment>
              {option.businessType && (
                <span style={{ display: 'block', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                  {option.businessType}
                </span>
              )}
              {addressText && (
                <span style={{ display: 'flex', alignItems: 'center', marginTop: '4px', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                  <LocationOnIcon fontSize="small" color="action" style={{ marginRight: '4px' }} />
                  {addressText}
                </span>
              )}
            </React.Fragment>
          }
        />
      </ListItem>
    );
  }, [defaultGetOptionLabel]);

  // Perform search with debouncing
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      // Don't search if query is too short
      if (!query || query.length < minChars) {
        setOptions([]);
        setLoading(false);
        return;
      }

      // Cancel any pending requests
      if (searchAbortController.current) {
        searchAbortController.current.abort();
      }

      // Create new abort controller for this request
      searchAbortController.current = new AbortController();
      
      setLoading(true);
      setError(null);
      
      // Log the API URL for debugging
      const apiUrl = `${config.apiUrl}/api${endpoint}`;
      
      try {
        const response = await axios.get(apiUrl, {
          params: { 
            query, 
            limit: limitResults
          },
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: searchAbortController.current.signal
        });
        
        if (response.data && response.data.success) {
          setOptions(response.data.data || []);
          setTotalResults(response.data.total || response.data.data?.length || 0);
        } else {
          console.warn('Search request succeeded but returned no successful response:', response.data);
          setOptions([]);
          setTotalResults(0);
          setError(response.data?.message || 'Failed to fetch results');
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          // Request was aborted, don't set error state
          return;
        }
        console.error('Search error:', error);
        console.error('Error details:', error.response?.data || 'No response data');
        setOptions([]);
        setTotalResults(0);
        setError(error.message || 'An error occurred during search');
      } finally {
        setLoading(false);
      }
    }, debounceMs),
    [token, endpoint, limitResults, minChars, debounceMs]
  );

  // Trigger search when input changes
  useEffect(() => {
    debouncedSearch(inputValue);
  }, [inputValue, debouncedSearch]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (searchAbortController.current) {
        searchAbortController.current.abort();
      }
    };
  }, []);

  // Custom Popper component to make dropdown more visible
  const CustomPopper = useCallback(props => (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={[
        {
          name: 'flip',
          enabled: true,
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            boundariesElement: 'viewport',
          },
        }
      ]}
    >
      <Paper elevation={4}>
        {props.children}
        {totalResults > limitResults && (
          <Box p={1} textAlign="center" borderTop={1} borderColor="divider">
            <Typography variant="caption" color="textSecondary">
              Showing {options.length} of {totalResults} results
            </Typography>
          </Box>
        )}
      </Paper>
    </Popper>
  ), [options.length, totalResults, limitResults]);

  return (
    <Autocomplete
      id="enhanced-search"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      inputValue={inputValue}
      onChange={(_, newValue) => {
        if (onSelect) {
          onSelect(newValue); // Call onSelect with newValue (which can be null when cleared)
        }
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={getOptionLabel || defaultGetOptionLabel}
      renderOption={renderOption || defaultRenderOption}
      options={options}
      loading={loading}
      filterOptions={filterOptions || ((x) => x)} // Don't filter on client side by default
      onInputChange={(_, newInputValue, reason) => {
        // Update inputValue on typing, clearing, OR selecting an option ('reset')
        if (reason === 'input' || reason === 'clear' || reason === 'reset') {
          setInputValue(newInputValue);
        }
      }}
      PopperComponent={CustomPopper}
      noOptionsText={
        inputValue.length < minChars 
          ? `Type at least ${minChars} characters to search` 
          : loading 
            ? "Searching..." 
            : "No results found"
      }
      {...autocompleteProps}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          autoFocus={autoFocus}
          error={!!error}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          {...textFieldProps}
        />
      )}
    />
  );
}

EnhancedSearch.propTypes = {
  endpoint: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  minChars: PropTypes.number,
  debounceMs: PropTypes.number,
  onSelect: PropTypes.func,
  renderOption: PropTypes.func,
  getOptionLabel: PropTypes.func,
  filterOptions: PropTypes.func,
  autoFocus: PropTypes.bool,
  variant: PropTypes.string,
  size: PropTypes.string,
  fullWidth: PropTypes.bool,
  limitResults: PropTypes.number,
  textFieldProps: PropTypes.object,
  autocompleteProps: PropTypes.object
};

export default EnhancedSearch; 